import type { InputHTMLAttributes, ReactNode, Ref } from "react";
import { useId } from "react";

/**
 * A checkbox that is still a checkbox.
 *
 * ## The native input is kept, not replaced
 *
 * It is visually hidden rather than removed, and the box beside it is drawn
 * from its `:checked` state. That keeps every behaviour worth having - the
 * space bar, form submission, `aria-checked` without writing it, and
 * `getByRole('checkbox')` finding it - while none of the appearance is the
 * browser's any more.
 *
 * `opacity-0` and positioned over the box rather than `display: none`: a hidden
 * input is not focusable, so the whole thing would drop out of the tab order.
 */
export function Checkbox({
  label,
  hint,
  className = "",
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  label: ReactNode;
  hint?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}) {
  const id = useId();

  return (
    <div className={`flex items-start gap-2.5 ${className}`}>
      <span className="relative mt-0.5 flex h-[18px] w-[18px] shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="peer absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...rest}
        />

        {/*
          Drawn from the input's own state, so it cannot disagree with it. The
          tick is a rotated rectangle rather than an SVG - two borders and a
          transform, and no second file to keep in step with the palette.
        */}
        <span
          aria-hidden
          className="
            pointer-events-none flex h-[18px] w-[18px] items-center justify-center
            rounded-[5px] border border-line bg-app transition-colors
            peer-hover:border-line-lit
            peer-focus-visible:border-accent
            peer-checked:border-accent peer-checked:bg-accent
            peer-disabled:opacity-50
          "
        >
          <span className="mt-[-2px] h-[9px] w-[5px] rotate-45 border-b-2 border-r-2 border-accent-ink opacity-0 transition-opacity peer-checked:opacity-100" />
        </span>
      </span>

      <label htmlFor={id} className="cursor-pointer select-none text-sm text-ink">
        {label}
        {hint && (
          <span className="mt-0.5 block text-xs font-normal text-faint">
            {hint}
          </span>
        )}
      </label>
    </div>
  );
}
